import { EditRightAccess, ObjectRightType, ViewRightType, hasGlobalRightAccess } from "@dto/Users";
import Field, { TYPE_ITEMS_PERIOD_TYPE } from "@dto/fields";
import { Period, PeriodType, PeriodTypeDay, PeriodTypeMonth, PeriodTypeQuarter, PeriodTypeWeek, PeriodTypeYear } from "@dto/period";
import { nextPosition, prevPosition, samePosition } from "@utils/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataStore, useModelStore, useUserStore } from "src/store";
import { CellPosition, CellProps } from "./GridCell";
import { PeriodDayCell } from "./PeriodDayCell";
import { PeriodMonthCell } from "./PeriodMonthCell";
import { PeriodQuarterCell } from "./PeriodQuarterCell";
import { PeriodWeekCell } from "./PeriodWeekCell";
import { PeriodYearCell } from "./PeriodYearCell";

export interface PeriodCellProps {
    pathKey: string | null;
    field: Field;
    readOnly: boolean;
    period: Period | undefined;
    updatePeriod: (period: Period | undefined) => void;
    position: CellPosition | null;
    isEditing: boolean;
    onClick: (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => void;
    onEnterKeyPress: (event: React.KeyboardEvent) => void;
    searchValue: string;
    setSearchValue: (searchValue: string) => void;
    searchInputRef: React.RefObject<HTMLInputElement>;
    displaySearch?: boolean;
}

export interface PeriodEditionFormProps {
    field: Field | undefined,
    value: Period | undefined,
    updatePeriod: (period: Period | undefined) => void,
    searchValue: string,
    setSearchValue: ((searchValue: string) => void) | undefined,
    searchInputRef: React.RefObject<HTMLInputElement> | undefined,
    displaySearch?: boolean;
    hasValidation?: boolean;
    disabled?: boolean;
    className?: string;
}


export interface MainPeriodCellProps extends CellProps {
    displaySearch?: boolean;
}

export function PeriodCell({ pathKey, field, item, position, updateValue, isEditing, isFocused, inputRef, displaySearch = true, blurTriggered, pKey, currentAggregation }: MainPeriodCellProps) {

    const colNumber = useDataStore(state => state.colNumber);
    const edit = useDataStore(state => state.edit);
    const focus = useDataStore(state => state.focus);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [value, setValue] = useState<Period | undefined>(Period.fromObject(item.value));
    const [searchValue, setSearchValue] = useState("");
    const { t } = useTranslation();
    const userRole = useModelStore(state => state.userRole);
    const user = useUserStore(state => state.user);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const readOnly = item.readOnly || !hasEditAccess ;

    const periodType = useMemo(() => {
        return field.typeItems?.get(TYPE_ITEMS_PERIOD_TYPE) as PeriodType;
    }, [field]);

    useEffect(() => {
        if (item?.value !== value) {
            setValue(Period.fromObject(item.value));
        }
    }, [item]);

    useEffect(() => {
        if (isEditing) {
            // Seed the search with the character typed to start editing (stored
            // by the grid in editInitialKey) so the first letter isn't lost.
            // Only the searchable period types (quarter/month/week) use it; the
            // year/day variants edit through inputRef instead.
            const initialKey = useDataStore.getState().editInitialKey;
            if (initialKey && initialKey.length === 1) {
                setSearchValue(initialKey);
            }
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    }, [isEditing]);


    function onClick(event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) {
        if (readOnly) {
            return;
        }

        const currentFocusPos = useDataStore.getState().focusPos;
        const isSelected = currentFocusPos && currentFocusPos.length === 1 && samePosition(currentFocusPos[0], position);
        if (isSelected && !isEditing) {
            event.stopPropagation();
            event.preventDefault();
            edit(position);
        }
    }

    function validate(newPeriod: Period | undefined) {
        if (readOnly || !isEditing) {
            return;
        }

        // if the value is the same as the item value, do nothing
        if (newPeriod === value) {
            edit(position);
            return;
        }

        const newItem = { ...item, value: newPeriod };
        setValue(newPeriod);
        updateValue(newItem, newPeriod);
        setSearchValue("");
    }


    const period = Period.fromObject(value);

    function cancel() {
        setSearchValue("");
        focus(position);
    }


    function onEnterKeyPress(event: React.KeyboardEvent) {
        switch (event.key) {
            case "Backspace":
            case "Delete":
                if (!isEditing && !readOnly) {
                    validate(undefined);
                }
                break;
            case "Enter":
                event.preventDefault();
                if (!isEditing && !readOnly) {
                    edit(position);
                }
                break;
            case "Tab":
                let next = null;
                if (event.shiftKey) {
                    next = prevPosition(position, colNumber);
                } else {
                    next = nextPosition(position, colNumber);
                }
                focus(next);
                break;
            case "Escape":
                event.preventDefault();
                cancel();
                break;
            default:
                // Fallback when the trigger itself is focused (the grid normally
                // routes the first keystroke through GridCell, which seeds via
                // editInitialKey). The input is controlled, so seed the state —
                // assigning .value directly was the original lost-first-letter bug.
                if (!isEditing && !readOnly && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
                    setSearchValue(event.key);
                    edit(position);
                }
        }
    }

    const updatePeriod = (newPeriod: Period | undefined) => {
        setValue(newPeriod);
        updateValue(item, newPeriod);
    }

    const displayPeriodType = (item.aggregated) ? period?.type ?? periodType : periodType;

    switch (displayPeriodType) {
        case PeriodTypeYear:
            return (
                <PeriodYearCell pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} />
            )
        case PeriodTypeQuarter:
            return (
                <PeriodQuarterCell pathKey={pathKey} field={field} readOnly={readOnly} period={period} position={position} updatePeriod={updatePeriod} isEditing={isEditing} onClick={onClick} onEnterKeyPress={onEnterKeyPress} searchValue={searchValue} setSearchValue={setSearchValue} searchInputRef={searchInputRef} displaySearch={displaySearch} />
            )
        case PeriodTypeMonth:
            return (
                <PeriodMonthCell pathKey={pathKey} field={field} readOnly={readOnly} period={period} position={position} updatePeriod={updatePeriod} isEditing={isEditing} onClick={onClick} onEnterKeyPress={onEnterKeyPress} searchValue={searchValue} setSearchValue={setSearchValue} searchInputRef={searchInputRef} displaySearch={displaySearch} />
            )
        case PeriodTypeWeek:
            return (
                <PeriodWeekCell pathKey={pathKey} field={field} readOnly={readOnly} period={period} position={position} updatePeriod={updatePeriod} isEditing={isEditing} onClick={onClick} onEnterKeyPress={onEnterKeyPress} searchValue={searchValue} setSearchValue={setSearchValue} searchInputRef={searchInputRef} displaySearch={displaySearch} />
            )
        case PeriodTypeDay:
            return (
                <PeriodDayCell pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} />
            )
        default:
            return (
                <div>
                    {period?.toString(t)}
                </div>
            )
    }

}
